import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";

/**
 * D4 — the single reusable "Link to…" primitive. Fetches candidate entities of a
 * type from an operator-API endpoint and returns the selected id(s). Used everywhere
 * a relationship is established (asset→campaign, deliverable→source meeting,
 * meeting→campaign, doc→foundation, review→asset).
 */

interface PickerItem {
  id: string;
  label: string;
  sublabel?: string;
}

interface EntityPickerProps {
  title: string;
  /** Operator-API path returning { items: [...] }, e.g. `/meetings?client=x`. */
  endpoint: string;
  /** Map a raw API item → { id, label, sublabel }. */
  mapItem: (raw: any) => PickerItem;
  multiple?: boolean;
  selected?: string[];
  confirmLabel?: string;
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
  /** Optional content rendered above the candidate list — e.g. the Send-for-Review
   *  Situation/Impact/statement fields. The parent owns that state and reads it in
   *  onConfirm; the picker just renders the slot. Keeps one shared picker for both
   *  Send-for-Review (with S/I/R) and send-back (plain). */
  extraTop?: React.ReactNode;
}

export function EntityPicker({ title, endpoint, mapItem, multiple = false, selected = [], confirmLabel = "Link", onConfirm, onClose, extraTop }: EntityPickerProps) {
  const [items, setItems] = useState<PickerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set(selected));

  useEffect(() => {
    fetch(`${DOCS_API_DOMAIN}/api/agents${endpoint}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setItems((d.items || []).map(mapItem)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const filtered = q.trim()
    ? items.filter((i) => `${i.label} ${i.sublabel || ""}`.toLowerCase().includes(q.toLowerCase()))
    : items;

  const toggle = (id: string) => {
    setChosen((prev) => {
      const n = new Set(multiple ? prev : []);
      if (prev.has(id) && multiple) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-black/[0.06] px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        {extraTop && <div className="border-b border-black/[0.06] px-4 py-3">{extraTop}</div>}
        <div className="border-b border-black/[0.04] p-2">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full rounded-lg border border-black/[0.06] px-3 py-1.5 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <p className="px-4 py-6 text-center text-[13px] text-gray-400">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-gray-400">Nothing to link.</p>
          ) : (
            filtered.map((it) => {
              const isOn = chosen.has(it.id);
              return (
                <button
                  key={it.id}
                  onClick={() => toggle(it.id)}
                  className={`flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-gray-50 ${isOn ? "bg-blue-50/60" : ""}`}
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isOn ? "border-blue-500 bg-blue-500 text-white" : "border-gray-300"}`}>
                    {isOn && (
                      <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-gray-900">{it.label}</span>
                    {it.sublabel && <span className="block truncate text-[11px] text-gray-400">{it.sublabel}</span>}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] px-4 py-3">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-700">Cancel</button>
          <button
            onClick={() => { onConfirm([...chosen]); }}
            disabled={chosen.size === 0}
            className="rounded-lg bg-gray-900 px-3.5 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all hover:bg-gray-800 disabled:opacity-40"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
